"use client";

import { useCallback } from "react";
import { useCreditGuardContext } from "@/components/credit-guard/CreditGuardProvider";
import type {
  CreditCheckOptions,
  CreditOperation,
} from "@/lib/types/credit-guard.types";

/**
 * The credit guard, for anything that spends credits.
 *
 * ```tsx
 * const { checkCredits } = useCreditGuard();
 *
 * const { canProceed } = await checkCredits({ operation: "email-unlock" });
 * if (!canProceed) return; // the modal has already explained why
 * ```
 *
 * Or, when the spend is a single call you can wrap:
 *
 * ```tsx
 * const { guardedAction } = useCreditGuard();
 * await guardedAction("contact-unlock", () => saveContacts(ids), { count: ids.length });
 * ```
 *
 * Must be called under <CreditGuardProvider>, which the authenticated shell
 * mounts (client-layout.tsx) — so anywhere inside the app, but not on /auth.
 */
export function useCreditGuard() {
  const context = useCreditGuardContext();
  const { checkCredits } = context;

  /**
   * Runs `action` only if the operation is affordable. Returns whether it ran,
   * so a caller can tell "blocked" from "done" without reading the result.
   */
  const guardedAction = useCallback(
    async (
      operation: CreditOperation,
      action: () => void | Promise<void>,
      options?: Omit<CreditCheckOptions, "operation">
    ): Promise<boolean> => {
      const result = await checkCredits({ operation, ...options });
      if (!result.canProceed) return false;
      await action();
      return true;
    },
    [checkCredits]
  );

  return { ...context, guardedAction };
}

export default useCreditGuard;
